import React, { useState, useEffect } from "react";
import { PlayCircle, X } from "lucide-react";

const STORAGE_KEY = "msp_getting_started_seen";

/**
 * Reusable "Getting Started" video guide button + modal.
 *
 * Props:
 * - short (bool, default false): shorter button label for tight spaces (e.g. sidebars, cards).
 * - autoOpen (bool, default false): if true, auto-opens the modal once per browser
 *   (tracked via localStorage) the first time this component mounts with autoOpen=true.
 *   Only one instance across the app should set autoOpen, otherwise multiple instances
 *   could each try to claim the "first open" — they all share the same localStorage key,
 *   so only the first one to mount will actually open it, but it's cleaner to only set
 *   autoOpen on one page (e.g. the home page).
 * - className (string, optional): extra classes for the trigger button, in case you need
 *   to adjust width/margins for the spot you're dropping it into.
 */
const GettingStartedGuide = ({ short = false, autoOpen = false, className = "" }) => {
    const [showHelpVideo, setShowHelpVideo] = useState(false);

    useEffect(() => {
        if (!autoOpen) return;
        try {
            const alreadySeen = localStorage.getItem(STORAGE_KEY);
            if (!alreadySeen) {
                setShowHelpVideo(true);
                localStorage.setItem(STORAGE_KEY, "1");
            }
        } catch (e) {
            // localStorage unavailable (private browsing, etc.) — just skip auto-open
        }
    }, [autoOpen]);

    return (
        <>
            <button
                onClick={() => setShowHelpVideo(true)}
                className={`flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl py-3 px-4 text-sm font-bold hover:opacity-90 transition shadow-md ${short ? "" : "w-full"} ${className}`}
            >
                <PlayCircle size={18} />
                {short ? "Getting Started Guide" : "Click here to see how to use MSP Inventory correctly & import all costs for accurate reports"}
            </button>

            {showHelpVideo && (
                <div
                    className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
                    onClick={() => setShowHelpVideo(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-xl max-w-4xl w-full p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-gray-800">Getting Set Up with MSP Inventory</h3>
                            <button onClick={() => setShowHelpVideo(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-3">
                            <p className="text-sm font-bold text-teal-800">
                                📍 Start at 16:30 for the step-by-step walkthrough of importing your costs.
                            </p>
                        </div>
                        <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                            <iframe
                                className="absolute inset-0 w-full h-full rounded-lg"
                                src="https://www.youtube.com/embed/naK-yJUov8A?autoplay=1&start=990"
                                title="MSP Inventory Setup Guide"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GettingStartedGuide;